import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import { Badge } from "./ui/badge";
import { 
  X, 
  Clock,
  PlayCircle,
  Dumbbell,
  Timer,
  Target,
  Lightbulb,
  Info,
  ChevronRight,
  Trophy,
  TrendingUp,
  Video,
  AlertCircle,
  CheckCircle2,
  Volume2,
  VolumeX
} from "lucide-react";
import { cn } from "../lib/utils";

// Colores por categoría
const CATEGORY_COLORS = {
  pierna: "#22C55E",
  gluteo: "#EC4899",
  pecho: "#3B82F6",
  espalda: "#8B5CF6",
  hombro: "#F59E0B",
  brazo: "#EF4444",
  core: "#06B6D4",
  cardio: "#10B981",
  fullbody: "#D4AF37",
};

// Nombres legibles para músculos
const MUSCLE_NAMES = {
  cuadriceps: "Cuádriceps",
  isquiotibiales: "Isquiotibiales",
  gluteos: "Glúteos",
  gemelos: "Gemelos",
  aductores: "Aductores",
  abductores: "Abductores",
  pecho: "Pecho",
  espalda_alta: "Espalda Alta",
  espalda_baja: "Espalda Baja",
  dorsales: "Dorsales",
  hombros: "Hombros",
  biceps: "Bíceps",
  triceps: "Tríceps",
  antebrazos: "Antebrazos",
  core: "Core",
  oblicuos: "Oblicuos",
  lumbares: "Lumbares",
  trapecio: "Trapecio",
};

/**
 * Modal/Vista de detalle completa de un ejercicio
 * Muestra: nombre, sets/reps, técnica, RIR, tips, descripción
 * Video integrado (placeholder si no hay) - NO abre YouTube
 * Totalmente scrolleable para contenido largo
 */
const ExerciseDetailModal = ({ 
  exercise, 
  onClose,
  onStartTracking,
  onStartTimer,
  isCompleted = false
}) => {
  const [videoError, setVideoError] = useState(false);
  const [videoLoading, setVideoLoading] = useState(true);
  const [videoReady, setVideoReady] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [useFallbackUrl, setUseFallbackUrl] = useState(false);
  const videoRef = useRef(null);
  
  if (!exercise) return null;
  
  // Parsear notas para extraer RIR y técnica
  const parseNotes = (notes) => {
    if (!notes) return { technique: null, rir: null };
    
    const parts = notes.split('|').map(p => p.trim());
    let technique = parts[0] || null;
    let rir = null;
    
    // Buscar RIR en las partes
    for (const part of parts) {
      if (part.toLowerCase().includes('rir')) {
        rir = part;
      }
    }
    
    // Si el RIR estaba en la primera parte, ajustar técnica
    if (technique && technique.toLowerCase().includes('rir')) {
      technique = parts.length > 1 ? parts.slice(0, -1).join(' | ') : null;
    }
    
    return { technique, rir };
  };
  
  const { technique, rir } = parseNotes(exercise.notes);
  
  // Determinar si tiene video interno (no YouTube)
  const hasInternalVideo = exercise.video_url && 
    !exercise.video_url.includes("youtube") && 
    !exercise.video_url.includes("youtu.be") &&
    !videoError;
  
  // Obtener poster/thumbnail para lazy loading
  const posterUrl = exercise.video_poster || exercise.video_thumbnail || null;
  
  // Transformar URL de Cloudinary para mejor compatibilidad móvil
  const getVideoUrl = () => {
    if (!exercise.video_url) return null;
    
    // Si es URL de Cloudinary, optimizar para móvil
    if (exercise.video_url.includes('cloudinary.com')) {
      let url = exercise.video_url;
      
      // Asegurar que tiene f_mp4 para compatibilidad con todos los navegadores
      // El formato MP4 es universalmente soportado
      if (!url.includes('f_mp4')) {
        // Insertar f_mp4 después de /upload/
        url = url.replace('/upload/', '/upload/f_mp4,');
      }
      
      // Si tiene f_auto, reemplazar con f_mp4
      if (url.includes('f_auto')) {
        url = url.replace('f_auto', 'f_mp4');
      }
      
      console.log("Video URL transformed:", url);
      return url;
    }
    
    return exercise.video_url;
  };
  
  // También intentar URL original si la transformada falla
  const getOriginalVideoUrl = () => {
    // Si hay video_original_url, construir URL con f_mp4
    if (exercise.video_original_url && exercise.video_original_url.includes('cloudinary.com')) {
      let url = exercise.video_original_url;
      // Insertar transformación f_mp4 correctamente (después de /upload/ y antes de version/folder)
      if (!url.includes('f_mp4')) {
        // Pattern: /upload/v123456/ -> /upload/f_mp4/v123456/
        url = url.replace('/upload/v', '/upload/f_mp4/v');
        // If no version, insert after /upload/
        if (!url.includes('/upload/f_mp4/v')) {
          url = url.replace('/upload/', '/upload/f_mp4/');
        }
      }
      console.log("Fallback video URL:", url);
      return url;
    }
    return null;
  };
  
  const videoUrl = getVideoUrl();
  const fallbackVideoUrl = getOriginalVideoUrl();
  
  const videoStatus = exercise.video_status || "coming_soon";
  const categoryColor = CATEGORY_COLORS[exercise.category] || "#D4AF37";
  const primaryMuscleName = MUSCLE_NAMES[exercise.primary_muscle] || 
                           MUSCLE_NAMES[exercise.muscle_group?.toLowerCase()] || 
                           exercise.primary_muscle || 
                           exercise.muscle_group || 
                           "N/A";
  
  // Video play/pause handler
  const handleVideoClick = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
    }
  };
  
  // Toggle mute
  const toggleMute = (e) => {
    e.stopPropagation();
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };
  
  const modalContent = (
    <div className="fixed inset-0 z-[100] bg-[#0B0B0B] flex flex-col animate-fade-in">
      {/* Header fijo con safe area - destacado con color de categoría */}
      <div 
        className="flex-shrink-0 p-4 border-b border-[#2A2A2A] bg-[#0B0B0B]" 
        style={{ 
          paddingTop: 'calc(max(env(safe-area-inset-top), 12px) + 8px)',
          background: `linear-gradient(135deg, ${categoryColor}10 0%, #0B0B0B 100%)`
        }}
      >
        <div className="flex items-center justify-between">
          <div className="flex-1 min-w-0 pr-4">
            <div className="flex items-center gap-2 mb-1">
              <h1 className="text-xl font-heading font-bold text-white truncate">
                {exercise.name}
              </h1>
              {exercise.category && (
                <Badge 
                  className="text-xs flex-shrink-0"
                  style={{ 
                    backgroundColor: `${categoryColor}20`,
                    borderColor: `${categoryColor}50`,
                    color: categoryColor
                  }}
                >
                  {exercise.category}
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-3 text-sm">
              <span className="text-[#D4AF37] font-semibold">
                {exercise.sets} × {exercise.reps}
              </span>
              <span className="flex items-center gap-1 text-[#888]">
                <Clock className="w-3.5 h-3.5" />
                {exercise.rest_seconds}s
              </span>
              {primaryMuscleName !== "N/A" && (
                <span className="flex items-center gap-1 text-[#888]">
                  <Target className="w-3.5 h-3.5" style={{ color: categoryColor }} />
                  {primaryMuscleName}
                </span>
              )}
            </div>
          </div>
          <button 
            onClick={onClose}
            className="p-3 bg-[#1A1A1A] hover:bg-[#2A2A2A] rounded-xl transition-colors flex-shrink-0 min-w-[44px] min-h-[44px] flex items-center justify-center border border-[#3A3A3A]"
            data-testid="close-detail-btn"
          >
            <X className="w-6 h-6 text-white" />
          </button>
        </div>
      </div>
      
      {/* Contenido scrolleable */}
      <div className="flex-1 overflow-y-auto">
        <div className="p-4 space-y-4 pb-32">
          
          {/* Video Section - INTEGRADO, NO EXTERNO */}
          <div className="aspect-[9/16] max-h-[400px] bg-[#1A1A1A] rounded-xl overflow-hidden border border-[#2A2A2A] relative">
            {hasInternalVideo ? (
              <>
                <video 
                  ref={videoRef}
                  src={useFallbackUrl ? fallbackVideoUrl : videoUrl} 
                  poster={posterUrl}
                  className="w-full h-full object-contain"
                  playsInline
                  webkit-playsinline="true"
                  controls
                  controlsList="nodownload"
                  preload="auto"
                  onLoadStart={() => {
                    setVideoLoading(true);
                    setVideoReady(false);
                    console.log("Video loading started:", useFallbackUrl ? fallbackVideoUrl : videoUrl);
                  }}
                  onCanPlay={() => {
                    setVideoLoading(false);
                    setVideoReady(true);
                    console.log("Video can play");
                  }}
                  onLoadedData={() => {
                    console.log("Video loaded data");
                    setVideoLoading(false);
                    setVideoReady(true);
                  }}
                  onError={(e) => {
                    const video = e.target;
                    const error = video.error;
                    console.error("Video error details:", {
                      code: error?.code,
                      message: error?.message,
                      videoUrl: useFallbackUrl ? fallbackVideoUrl : videoUrl,
                      networkState: video.networkState,
                      readyState: video.readyState
                    });
                    
                    // Try fallback URL if available and not already using it
                    if (!useFallbackUrl && fallbackVideoUrl) {
                      console.log("Trying fallback video URL:", fallbackVideoUrl);
                      setUseFallbackUrl(true);
                      setVideoLoading(true);
                      return;
                    }
                    
                    // Set error
                    setVideoError(true);
                    setVideoLoading(false);
                  }}
                />
                
                {/* Loading overlay */}
                {videoLoading && !videoReady && !videoError && (
                  <div className="absolute inset-0 flex flex-col items-center justify-center bg-[#1A1A1A]/80 pointer-events-none">
                    <div className="w-10 h-10 border-3 border-[#D4AF37] border-t-transparent rounded-full animate-spin mb-3" />
                    <p className="text-xs text-muted-foreground">Cargando video...</p>
                  </div>
                )}
                
                {/* Error state overlay */}
                {videoError && (
                  <div className="absolute inset-0 flex flex-col items-center justify-center bg-[#1A1A1A]">
                    <Video className="w-12 h-12 text-red-400 mb-3" />
                    <p className="text-sm text-red-400">Error al cargar video</p>
                    <p className="text-xs text-muted-foreground mt-1">Prueba recargar la página</p>
                  </div>
                )}
                
                {/* HD badge */}
                {videoReady && (
                  <div className="absolute top-3 left-3 z-10 pointer-events-none">
                    <Badge className="bg-green-500/20 text-green-400 border-green-500/30 text-xs">
                      HD
                    </Badge>
                  </div>
                )}
              </>
            ) : (
              <div className="w-full h-full flex flex-col items-center justify-center text-center p-4">
                <div 
                  className="w-16 h-16 rounded-full flex items-center justify-center mb-3"
                  style={{ backgroundColor: `${categoryColor}15` }}
                >
                  <Video className="w-8 h-8" style={{ color: categoryColor }} />
                </div>
                <p className="font-semibold" style={{ color: categoryColor }}>Video Próximamente</p>
                <p className="text-xs text-muted-foreground mt-1 max-w-[200px]">
                  Video de técnica profesional en desarrollo
                </p>
              </div>
            )}
          </div>

          {/* Secondary Muscles */}
          {exercise.secondary_muscles?.length > 0 && (
            <div>
              <p className="text-xs text-[#888] mb-2 flex items-center gap-1">
                <Dumbbell className="w-3 h-3" />
                Músculos Secundarios
              </p>
              <div className="flex flex-wrap gap-2">
                {exercise.secondary_muscles.map((muscle) => (
                  <Badge 
                    key={muscle} 
                    variant="outline" 
                    className="text-xs border-[#2A2A2A] text-white/70"
                  >
                    {MUSCLE_NAMES[muscle] || muscle}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {/* Tracking info si existe */}
          {(exercise.tracking || exercise.personal_record) && (
            <Card className="bg-[#1A1A1A] border-[#2A2A2A]">
              <CardContent className="p-4">
                <h3 className="text-sm font-semibold text-[#888] uppercase tracking-wider mb-3 flex items-center gap-2">
                  <TrendingUp className="w-4 h-4" />
                  Tu Progreso
                </h3>
                <div className="grid grid-cols-2 gap-3">
                  {exercise.tracking && (
                    <div className="bg-[#252525] rounded-xl p-3">
                      <p className="text-xs text-[#666] mb-1">Último entrenamiento</p>
                      <p className="text-white font-bold text-lg">
                        {exercise.tracking.last_weight}kg
                      </p>
                      <p className="text-xs text-[#888]">
                        × {exercise.tracking.last_reps} reps • {exercise.tracking.last_date}
                      </p>
                    </div>
                  )}
                  {exercise.personal_record && (
                    <div className="bg-[#252525] rounded-xl p-3 border border-[#F59E0B]/30">
                      <p className="text-xs text-[#F59E0B] mb-1 flex items-center gap-1">
                        <Trophy className="w-3 h-3" />
                        Récord Personal
                      </p>
                      <p className="text-[#F59E0B] font-bold text-lg">
                        {exercise.personal_record.max_weight}kg
                      </p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
          
          {/* Technique Explanation from Library */}
          {exercise.technique_explanation && (
            <Card className="bg-[#1A1A1A] border-[#2A2A2A]">
              <CardContent className="p-4">
                <h3 className="text-sm font-semibold text-[#888] uppercase tracking-wider mb-3 flex items-center gap-2">
                  <Target className="w-4 h-4" style={{ color: categoryColor }} />
                  Técnica de Ejecución
                </h3>
                <p className="text-white text-base leading-relaxed">
                  {exercise.technique_explanation}
                </p>
              </CardContent>
            </Card>
          )}
          
          {/* Técnica de ejecución from notes (legacy) */}
          {!exercise.technique_explanation && technique && (
            <Card className="bg-[#1A1A1A] border-[#2A2A2A]">
              <CardContent className="p-4">
                <h3 className="text-sm font-semibold text-[#888] uppercase tracking-wider mb-3 flex items-center gap-2">
                  <Target className="w-4 h-4 text-[#D4AF37]" />
                  Técnica de Ejecución
                </h3>
                <p className="text-white text-base leading-relaxed">
                  {technique}
                </p>
              </CardContent>
            </Card>
          )}

          {/* Common Errors */}
          {exercise.common_errors?.length > 0 && (
            <Card className="bg-[#1A1A1A] border-[#2A2A2A]">
              <CardContent className="p-4">
                <h3 className="text-sm font-semibold text-[#888] uppercase tracking-wider mb-3 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 text-red-400" />
                  Errores Comunes
                </h3>
                <div className="space-y-2">
                  {exercise.common_errors.map((error, idx) => (
                    <div 
                      key={idx} 
                      className="flex items-start gap-2 bg-red-500/10 border border-red-500/20 rounded-lg p-2"
                    >
                      <X className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-white/80">{error}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Execution Tips */}
          {exercise.execution_tips?.length > 0 && (
            <Card className="bg-[#1A1A1A] border-[#2A2A2A]">
              <CardContent className="p-4">
                <h3 className="text-sm font-semibold text-[#888] uppercase tracking-wider mb-3 flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-400" />
                  Consejos de Ejecución
                </h3>
                <div className="space-y-2">
                  {exercise.execution_tips.map((tip, idx) => (
                    <div 
                      key={idx} 
                      className="flex items-start gap-2 bg-green-500/10 border border-green-500/20 rounded-lg p-2"
                    >
                      <CheckCircle2 className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-white/80">{tip}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
          
          {/* RIR recomendado */}
          {rir && (
            <Card className="bg-[#1A1A1A] border-[#2A2A2A]">
              <CardContent className="p-4">
                <h3 className="text-sm font-semibold text-[#888] uppercase tracking-wider mb-3 flex items-center gap-2">
                  <Dumbbell className="w-4 h-4 text-[#22C55E]" />
                  Intensidad Recomendada
                </h3>
                <div className="bg-[#22C55E]/10 border border-[#22C55E]/30 rounded-xl p-3">
                  <p className="text-[#22C55E] font-bold text-lg">
                    {rir}
                  </p>
                  <p className="text-xs text-[#888] mt-1">
                    RIR = Repeticiones en Reserva (cuántas más podrías hacer)
                  </p>
                </div>
              </CardContent>
            </Card>
          )}
          
          {/* Tips adicionales (legacy) */}
          {exercise.tips && !exercise.execution_tips?.length && (
            <Card className="bg-[#1A1A1A] border-[#2A2A2A]">
              <CardContent className="p-4">
                <h3 className="text-sm font-semibold text-[#888] uppercase tracking-wider mb-3 flex items-center gap-2">
                  <Lightbulb className="w-4 h-4 text-yellow-400" />
                  Consejos
                </h3>
                <p className="text-white text-base leading-relaxed">
                  {exercise.tips}
                </p>
              </CardContent>
            </Card>
          )}
          
          {/* Descripción */}
          {exercise.description && !exercise.technique_explanation && (
            <Card className="bg-[#1A1A1A] border-[#2A2A2A]">
              <CardContent className="p-4">
                <h3 className="text-sm font-semibold text-[#888] uppercase tracking-wider mb-3 flex items-center gap-2">
                  <Info className="w-4 h-4 text-blue-400" />
                  Descripción
                </h3>
                <p className="text-[#AAA] text-base leading-relaxed">
                  {exercise.description}
                </p>
              </CardContent>
            </Card>
          )}
          
          {/* Notas completas si no se parsearon bien */}
          {exercise.notes && !technique && !rir && (
            <Card className="bg-[#1A1A1A] border-[#2A2A2A]">
              <CardContent className="p-4">
                <h3 className="text-sm font-semibold text-[#888] uppercase tracking-wider mb-3 flex items-center gap-2">
                  <Lightbulb className="w-4 h-4 text-[#D4AF37]" />
                  Notas
                </h3>
                <p className="text-white text-base leading-relaxed">
                  {exercise.notes}
                </p>
              </CardContent>
            </Card>
          )}
          
          {/* Equipamiento */}
          {exercise.equipment && (
            <Card className="bg-[#1A1A1A] border-[#2A2A2A]">
              <CardContent className="p-4">
                <h3 className="text-sm font-semibold text-[#888] uppercase tracking-wider mb-2">
                  Equipamiento
                </h3>
                <p className="text-white capitalize">
                  {exercise.equipment.replace(/_/g, ' ')}
                </p>
              </CardContent>
            </Card>
          )}
          
        </div>
      </div>
      
      {/* Botones de acción fijos en la parte inferior */}
      <div className="flex-shrink-0 p-4 border-t border-[#2A2A2A] bg-[#0B0B0B] safe-area-bottom">
        <div className="flex gap-3">
          {/* Botón de tracking */}
          <Button
            onClick={() => {
              onClose();
              if (onStartTracking) onStartTracking();
            }}
            className="flex-1 h-12 bg-[#D4AF37] hover:bg-[#E5C54D] text-[#0B0B0B] font-bold rounded-xl"
            data-testid="detail-tracking-btn"
          >
            <Dumbbell className="w-5 h-5 mr-2" />
            Registrar Series
          </Button>
        </div>
        
        {/* Botón de timer */}
        {exercise.rest_seconds > 0 && (
          <button
            onClick={() => {
              if (onStartTimer) onStartTimer(exercise.rest_seconds);
            }}
            className="w-full mt-3 h-10 bg-[#252525] hover:bg-[#303030] rounded-xl flex items-center justify-center gap-2 text-[#888] hover:text-white transition-colors"
            data-testid="detail-timer-btn"
          >
            <Timer className="w-4 h-4" />
            Timer de descanso ({exercise.rest_seconds}s)
          </button>
        )}
      </div>
    </div>
  );
  
  // Render using portal to escape layout stacking context
  return createPortal(modalContent, document.body);
};

export default ExerciseDetailModal;
